import React, {useState} from "react";
import {
    AppBar,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Toolbar,
    Typography,
    Collapse,
    Menu,
    MenuItem
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavButton from "../menu/NavButton";
import logo from "../../assets/logo512.png";
import {Link, NavLink} from "react-router-dom";
import UserMenu from "../menu/UserMenu";
import UserRole from "../../schemas/user/roles";
import {useAuth} from "../../utils/hooks/useAuth";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

type MenuItemType = {
    name: string;
    to: string;
    allowedRole?: UserRole;
    menuItems?: MenuItemType[];
}

const menuItems: MenuItemType[] = [
    {
        name: "Závody", to: "/", menuItems: [
            {name: "Správa", to: "/races", allowedRole: UserRole.COMMODORE},
            {name: "Pořádání", to: "/races/organize", allowedRole: UserRole.ORGANIZER},
        ]
    },
    {name: "Výsledky", to: "/results"},
    {name: "Sezóny", to: "/seasons", allowedRole: UserRole.COMMODORE},
    {name: "Lodě", to: "/boats"},
    {name: "Kategorie", to: "/categories"},
    {name: "Kluby", to: "/clubs"},
    {name: "Uživatelé", to: "/users", allowedRole: UserRole.USER},
];

const Header: React.FC = () => {
    const {hasPermission} = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openSubMenu, setOpenSubMenu] = useState<{ [key: string]: boolean }>({});
    const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (key: string, event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(prevState => ({
            ...prevState,
            [key]: event.currentTarget,
        }));
    };

    const handleMenuClose = (key: string) => {
        setAnchorEl(prevState => ({
            ...prevState,
            [key]: null,
        }));
    }

    const handleSubMenuToggle = (itemName: string) => {
        setOpenSubMenu(prevState => ({
            [itemName]: !prevState[itemName],
        }));
    };

    const drawer = (
        <Box sx={{textAlign: 'center'}}>
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2}}>
                <Link to="/" style={{textDecoration: "none", color: "white"}}>
                    <img src={logo} alt="Logo" style={{width: 55, height: 55}} aria-label="Logo"/>
                </Link>
            </Box>
            <Box sx={{display: 'flex', pl: 3, py: 1, backgroundColor: "primary.main"}}>
                <UserMenu onClick={handleDrawerToggle}/>
            </Box>
            <Divider/>
            <List>
                {menuItems
                    .filter(item => !item.allowedRole || hasPermission(item.allowedRole))
                    .map((item) => (
                        item.menuItems
                        && item.menuItems.filter(subItem => !subItem.allowedRole || hasPermission(subItem.allowedRole)).length > 0 ? (
                            <React.Fragment key={item.name}>
                                <ListItemButton onClick={() => handleSubMenuToggle(item.name)}>
                                    <ListItemText primary={item.name}/>
                                    {openSubMenu[item.name] ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                                </ListItemButton>
                                <Collapse in={openSubMenu[item.name]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        <ListItemButton
                                            key={item.name}
                                            component={NavLink}
                                            to={item.to}
                                            onClick={handleDrawerToggle}
                                            sx={{
                                                pl: 4,
                                                '&.active': {
                                                    fontWeight: 'bold',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                },
                                            }}
                                        >
                                            <ListItemText primary={item.name} secondary="Hlavní stránka"/>
                                        </ListItemButton>
                                        {item.menuItems
                                            .filter(subItem => !subItem.allowedRole || hasPermission(subItem.allowedRole))
                                            .map((subItem) => (
                                                <ListItemButton
                                                    key={subItem.name}
                                                    component={NavLink}
                                                    to={subItem.to}
                                                    onClick={handleDrawerToggle}
                                                    sx={{
                                                        pl: 4,
                                                        ...(subItem.to === window.location.pathname && {
                                                            fontWeight: 'bold',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        }),
                                                    }}
                                                >
                                                    <ListItemText primary={subItem.name}/>
                                                </ListItemButton>
                                            ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        ) : (
                            <ListItemButton
                                key={item.name}
                                component={NavLink}
                                to={item.to}
                                onClick={handleDrawerToggle}
                                sx={{
                                    '&.active': {
                                        fontWeight: 'bold',
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                    },
                                }}
                            >
                                <ListItemText primary={item.name}/>
                            </ListItemButton>
                        )
                    ))}
            </List>
        </Box>
    );

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Box sx={{display: 'flex', alignItems: 'center', flexGrow: 1}}>
                        <Link to="/" style={{textDecoration: "none", color: "white"}}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <img src={logo} alt={"Logo"}
                                     style={{width: 55, height: 55, marginRight: 16}} aria-label={"Logo"}/>
                                <Typography variant="h6" sx={{flexGrow: 1}} fontWeight="bold">
                                    Slapský pohár – App
                                </Typography>
                            </Box>
                        </Link>
                    </Box>
                    <Box sx={{display: {xs: 'none', md: 'flex'}}}>
                        {menuItems
                            .filter(item => !item.allowedRole || hasPermission(item.allowedRole))
                            .map((item) => (
                                <Box
                                    key={item.name}
                                    sx={{
                                        display: "flex",
                                        "&:hover": {
                                            backgroundColor: "rgba(255,255,255,0.1)",
                                        }
                                    }}>
                                    <NavButton name={item.name} to={item.to}/>
                                    {item.menuItems
                                        && item.menuItems.filter(subItem => !subItem.allowedRole ||
                                            hasPermission(subItem.allowedRole)).length > 0 && (
                                            <>
                                                <IconButton
                                                    onClick={(e) => handleMenuOpen(item.name, e)}
                                                    sx={{p: 0, ml: -1, color: "inherit"}}
                                                >
                                                    {anchorEl[item.name] ? <ArrowDropUpIcon/> : <ArrowDropDownIcon/>}
                                                </IconButton>
                                                <Menu
                                                    anchorEl={anchorEl[item.name]}
                                                    open={Boolean(anchorEl[item.name])}
                                                    onClose={() => handleMenuClose(item.name)}
                                                    MenuListProps={{
                                                        onMouseLeave: () => handleMenuClose(item.name),
                                                    }}
                                                >
                                                    {item.menuItems
                                                        .filter(subItem => !subItem.allowedRole || hasPermission(subItem.allowedRole))
                                                        .map((subItem) => (
                                                            <MenuItem
                                                                key={subItem.name}
                                                                component={NavLink}
                                                                to={subItem.to}
                                                                selected={subItem.to === window.location.pathname}
                                                                onClick={() => handleMenuClose(item.name)}
                                                            >
                                                                {subItem.name}
                                                            </MenuItem>
                                                        ))}
                                                </Menu>
                                            </>
                                        )}
                                </Box>
                            ))}
                        <UserMenu/>
                    </Box>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{display: {xs: 'block', md: 'none'}}}
                    >
                        <MenuIcon/>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                sx={{
                    '& .MuiDrawer-paper': {boxSizing: 'border-box', width: 250},
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Header;
